// Reports screen: pick sections, preview them as A4 pages, then print (or Save as
// PDF) and download one Excel workbook. Both outputs come from the same ReportDoc
// set, so the spreadsheet and the paper can't disagree.
import type { RenderedView } from '../router.js';
import { navigate } from '../router.js';
import { repos } from '../store.js';
import { esc, qs, qsa, onClick, toast } from '../dom.js';
import { icon } from '../icons.js';
import { analyzeQuiz } from '../../analysis/itemAnalysis.js';
import { analyzeCompetencies } from '../../analysis/competencyAnalysis.js';
import { competencyDoc, learnerMasteryDoc, itemAnalysisDoc, rawDataDoc, type ReportContext, type ReportDoc } from '../../export/reportModel.js';
import { docsToXlsx, docToPrintHtml } from '../../export/reportRender.js';
import { shareOrDownloadBytes, safeName } from '../../export/csv.js';
import { XLSX_MIME } from '../../export/xlsx.js';
import type { AnswerKeyRecord, CompetencyMapRecord, QuizRecord, ResultRecord, StudentRecord } from '../../db/schema.js';

const OPTS = ['A', 'B', 'C', 'D'];

type SectionId = 'competency' | 'learners' | 'items' | 'raw';

interface Section {
  id: SectionId;
  label: string;
  hint: string;
  /** on by default — the two reports teachers file most often. */
  on: boolean;
  available: boolean;
  why?: string;
  doc?: ReportDoc;
}

interface Ctx {
  quiz: QuizRecord;
  key: AnswerKeyRecord | null;
  map: CompetencyMapRecord | null;
  results: ResultRecord[];
  rawRows: Array<{ result: ResultRecord; section: string; date: string }>;
  students: StudentRecord[];
  reportCtx: ReportContext;
  subtitle: string;
  back: string;
  fileTag: string;
}

function buildSections(c: Ctx): Section[] {
  const analysis = c.key ? analyzeQuiz(c.results, c.key, OPTS) : null;
  const gap = c.key && c.map?.competencies.length ? analyzeCompetencies(c.results, c.key, c.map, c.students) : null;

  return [
    {
      id: 'competency',
      label: 'Item/Competency Analysis',
      hint: 'Per competency, with the remediation roster by intervention tier.',
      on: true,
      available: !!gap,
      why: !c.key ? 'needs an answer key' : 'needs a Table of Specifications',
      doc: gap ? competencyDoc(gap, c.reportCtx) : undefined,
    },
    {
      id: 'learners',
      label: 'Per-learner mastery',
      hint: 'One row per learner: mastered, least mastered and the response.',
      on: true,
      available: !!gap,
      why: !c.key ? 'needs an answer key' : 'needs a Table of Specifications',
      doc: gap ? learnerMasteryDoc(gap, c.reportCtx) : undefined,
    },
    {
      id: 'items',
      label: 'Item analysis & MPS',
      hint: 'Difficulty, discrimination, decisions and per-item MPS.',
      on: false,
      available: !!analysis,
      why: 'needs an answer key',
      doc: analysis ? itemAnalysisDoc(analysis, c.reportCtx) : undefined,
    },
    {
      id: 'raw',
      label: 'Raw scores & responses',
      hint: 'Every learner\'s score and chosen option per item, plus the key.',
      on: false,
      available: c.rawRows.length > 0,
      why: 'no papers scanned yet',
      doc: rawDataDoc(c.rawRows, c.students, c.key, c.reportCtx),
    },
  ];
}

function renderReports(c: Ctx): RenderedView {
  const sections = buildSections(c);
  const selected = new Set<SectionId>(sections.filter((s) => s.available && s.on).map((s) => s.id));
  let landscape = false;
  const s = c.reportCtx.settings;
  const missingHeader = !s.schoolName || !s.teacherName;

  const docsFor = (): ReportDoc[] =>
    sections.filter((x) => selected.has(x.id) && x.doc).map((x) => x.doc!);

  const checks = sections
    .map(
      (x) => `<label class="rp-pick${x.available ? '' : ' off'}">
        <input type="checkbox" data-sec="${x.id}" ${selected.has(x.id) ? 'checked' : ''} ${x.available ? '' : 'disabled'}>
        <span><strong>${esc(x.label)}</strong><span class="meta muted">${x.available ? esc(x.hint) : esc(x.why ?? 'unavailable')}</span></span>
      </label>`,
    )
    .join('');

  return {
    title: 'Reports',
    subtitle: c.subtitle,
    back: c.back,
    html: `<div class="view">
      <div class="card no-print"><div class="stack">
        <div><strong>Print or export</strong><span class="meta muted">A4 · tables · Excel</span></div>
        ${missingHeader
          ? `<span class="badge badge-warn">${icon.alert()} School / teacher name not set — the header and signature lines will be blank.</span>
             <button class="btn btn-ghost btn-sm btn-block" data-settings>${icon.edit()} Add school &amp; teacher details</button>`
          : ''}
        <div class="stack" style="margin-top:4px">${checks}</div>
        <label class="rp-pick"><input type="checkbox" data-landscape>
          <span><strong>Landscape</strong><span class="meta muted">Wider tables fit; portrait suits most reports.</span></span></label>
        <div class="row2">
          <button class="btn btn-primary btn-sm" data-print>${icon.doc()} Print / Save as PDF</button>
          <button class="btn btn-ghost btn-sm" data-xlsx>${icon.download()} Excel (.xlsx)</button>
        </div>
        <p class="hint">Print gives one A4 page per table, with the header row repeated and rows never split. Excel gives one sheet per table, already set to A4 fit-to-width.</p>
      </div></div>
      <p class="section-label no-print">Preview</p>
      <div id="rp-out"></div>
    </div>`,
    unmount() {
      document.getElementById('rp-page-style')?.remove();
    },
    mount(root) {
      const out = qs(root, '#rp-out')!;
      const draw = () => {
        const docs = docsFor();
        out.innerHTML = docs.length
          ? docs.map((d, i) => docToPrintHtml(d, { first: i === 0 })).join('')
          : `<p class="empty no-print">Nothing selected.</p>`;
      };
      // @page can't be toggled by a class, so the orientation lives in its own
      // stylesheet that we rewrite.
      const applyPage = () => {
        let el = document.getElementById('rp-page-style') as HTMLStyleElement | null;
        if (!el) {
          el = document.createElement('style');
          el.id = 'rp-page-style';
          document.head.appendChild(el);
        }
        el.textContent = `@page { size: A4 ${landscape ? 'landscape' : 'portrait'}; margin: ${landscape ? '10mm' : '12mm 10mm'}; }`;
      };
      draw();
      applyPage();

      qsa<HTMLInputElement>(root, '[data-sec]').forEach((box) =>
        box.addEventListener('change', () => {
          const id = box.dataset.sec as SectionId;
          if (box.checked) selected.add(id);
          else selected.delete(id);
          draw();
        }),
      );
      qs<HTMLInputElement>(root, '[data-landscape]')?.addEventListener('change', (e) => {
        landscape = (e.target as HTMLInputElement).checked;
        applyPage();
      });

      onClick(root, '[data-settings]', () => navigate('#/settings'));
      onClick(root, '[data-print]', () => {
        if (!selected.size) return toast('Select at least one report', 'err');
        window.print();
      });
      onClick(root, '[data-xlsx]', async () => {
        const docs = docsFor();
        if (!docs.length) return toast('Select at least one report', 'err');
        try {
          const bytes = docsToXlsx(docs);
          const file = `${safeName(c.quiz.title)}_${c.fileTag}_reports.xlsx`;
          const how = await shareOrDownloadBytes(file, bytes, XLSX_MIME);
          toast(how === 'shared' ? 'Shared Excel workbook' : 'Downloaded Excel workbook');
        } catch (e) {
          toast(e instanceof Error ? e.message : 'Export failed', 'err');
        }
      });
    },
  };
}

/** One class's run of a quiz. */
export async function assignmentReportsView(params: Record<string, string>): Promise<RenderedView> {
  const assignment = await repos.getAssignment(params.id);
  if (!assignment) return { title: 'Reports', back: '/quizzes', html: `<div class="view"><p class="empty">Not found.</p></div>` };
  const quiz = (await repos.getQuiz(assignment.quizId))!;
  const [key, map, results, students, cls, settings] = await Promise.all([
    repos.answerKeyFor(quiz.id),
    repos.competencyMapFor(quiz.id),
    repos.resultsForAssignment(assignment.id),
    repos.studentsInClass(assignment.classId),
    repos.getClass(assignment.classId),
    repos.getSettings(),
  ]);
  const section = cls?.name ?? '';
  return renderReports({
    quiz,
    key: key ?? null,
    map: map ?? null,
    results,
    rawRows: results.map((r) => ({ result: r, section, date: assignment.dateAdministered })),
    students,
    reportCtx: { quiz, settings, section, gradeLevel: cls?.gradeLevel ?? quiz.gradeLevel, date: assignment.dateAdministered },
    subtitle: `${quiz.title} · ${section}`,
    back: `/assignment/${assignment.id}/review`,
    fileTag: safeName(section || 'class'),
  });
}

/** Rolled up across every class that sat this quiz. */
export async function quizReportsView(params: Record<string, string>): Promise<RenderedView> {
  const quiz = await repos.getQuiz(params.id);
  if (!quiz) return { title: 'Reports', back: '/quizzes', html: `<div class="view"><p class="empty">Quiz not found.</p></div>` };
  const [key, map, assignments, classes, settings] = await Promise.all([
    repos.answerKeyFor(quiz.id),
    repos.competencyMapFor(quiz.id),
    repos.assignmentsForQuiz(quiz.id),
    repos.listClasses(),
    repos.getSettings(),
  ]);
  const classById = new Map(classes.map((x) => [x.id, x]));
  const asgById = new Map(assignments.map((a) => [a.id, a]));
  const results = await repos.resultsForQuiz(quiz.id);
  const students = (await Promise.all(assignments.map((a) => repos.studentsInClass(a.classId)))).flat();
  return renderReports({
    quiz,
    key: key ?? null,
    map: map ?? null,
    results,
    rawRows: results.map((r) => {
      const a = asgById.get(r.quizAssignmentId);
      const cls = a ? classById.get(a.classId) : undefined;
      return { result: r, section: cls?.name ?? '', date: a?.dateAdministered ?? '' };
    }),
    students,
    reportCtx: { quiz, settings, section: 'All classes', gradeLevel: quiz.gradeLevel, date: '' },
    subtitle: `${quiz.title} · all classes`,
    back: `/quiz/${quiz.id}`,
    fileTag: 'all',
  });
}
